import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:8000/api/v1`;

// Нужен снаружи там, где адрес открывают не через axios, а ссылкой в новой
// вкладке (руководство, печать бирки): собрать его надо от того же корня.
export const API_BASE_URL = API_BASE;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Интерфейс всегда русский — и серверные сообщения тоже
  config.headers['X-Lang'] = 'ru';

  // На какой цех человек сейчас смотрит.
  //
  // Роль тут больше ни при чём: переключаться вправе всякий, кому открыт не
  // один филиал, — директор, главный админ, бухгалтер. Кому открыт один,
  // ничего не выбирает и параметра не шлёт.
  //
  // Проверять его здесь незачем: сервер и так пускает только разрешённые, а
  // чужой номер не открывает ничего. Подставляем в перехватчике, а не в каждом
  // вызове: забыть один вызов — значит показать один экран от чужого цеха и
  // не понять, почему цифры не сходятся.
  const branch = sessionStorage.getItem('selected_branch') || 'all';
  if (branch && branch !== 'all') {
    config.params = { ...config.params, branch };
  }

  // Кто сейчас за прессом. Вход на моноблоке общий, человека называет ПИН при
  // старте работы — и выпуск должен записаться на него, а не на общую учётку.
  // Подставляем в перехватчике, а не в каждом вызове: забыть один вызов —
  // значит потерять авторство части смены, и заметят это только в отчёте.
  if (pressWorkerId && PRESS_PATHS.some(p => (config.url || '').includes(p))) {
    // Вместе с человеком отдаём и моноблок: по нему сервер отличит работу от
    // станка от работы из кабинета — и не по слову, а по тому, кто держит
    // пресс за собой.
    const add = { worker: pressWorkerId };
    if (pressClientId) add.client = pressClientId;
    if (config.data && typeof config.data === 'object') {
      config.data = { ...config.data, ...add };
    } else if (!config.data) {
      config.data = add;
    }
  }

  return config;
});

// Всё, где решается авторство работы у пресса: старт, счётчик, сорта,
// завершение, а также остановки и переналадка — их делает человек, и
// спросят про них по имени, а не по учётке моноблока.
const PRESS_PATHS = ['/production/queue/', '/production/press-jobs/',
                     '/production/presses/'];
let pressWorkerId = null;
export const setPressWorker = (id) => { pressWorkerId = id || null; };
export const getPressWorker = () => pressWorkerId;

// Идентификатор моноблока. Его же терминал предъявляет, когда занимает пресс.
let pressClientId = null;
export const setPressClient = (id) => { pressClientId = id || null; };

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      sessionStorage.clear();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);


// --- Паспорт изделия: вход сотрудника на самой странице ---
// Директор сканирует QR камерой телефона — каждый раз новая вкладка, а вход MES
// живёт в sessionStorage и до неё не доходит. Поэтому паспорт помнит свой вход
// в localStorage под СВОИМИ ключами (passport_*): не access_token, чтобы вход
// с паспорта не стал входом в MES на общем компьютере (тот же приём, что в /ai).
// Запросы идут мимо общего клиента `api`: его перехватчик на любой 401 стирает
// сессию и уводит на /login, а для паспорта просроченный токен — не повод.
const pub = axios.create({ baseURL: API_BASE, headers: { 'X-Lang': 'ru' } });
const PA = 'passport_access';
const PR = 'passport_refresh';
const PU = 'passport_user';

const ls = {
  get: (k) => { try { return localStorage.getItem(k) || ''; } catch (e) { return ''; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* нет хранилища */ } },
  del: (k) => { try { localStorage.removeItem(k); } catch (e) { /* нет */ } },
};
const ss = {
  get: (k) => { try { return sessionStorage.getItem(k) || ''; } catch (e) { return ''; } },
  set: (k, v) => { try { sessionStorage.setItem(k, v); } catch (e) { /* нет */ } },
};

// Откуда брать вход: MES (вкладка) → паспорт (телефон) → /ai.
const PASSPORT_SOURCES = [
  { name: 'mes',      get: ss.get, set: ss.set, a: 'access_token', r: 'refresh_token' },
  { name: 'passport', get: ls.get, set: ls.set, a: PA, r: PR, own: true },
  { name: 'ai',       get: ls.get, set: ls.set, a: 'ai_access', r: 'ai_refresh' },
];

async function refreshSource(src) {
  const r = src.get(src.r);
  if (!r) return false;
  try {
    const { data } = await pub.post('/auth/refresh/', { refresh: r });
    if (!data?.access) return false;
    src.set(src.a, data.access);
    // Ротация: сервер может выдать и новый refresh — старый тогда негоден.
    if (data.refresh) src.set(src.r, data.refresh);
    return true;
  } catch (e) { return false; }
}

async function passportFetch(qr, key) {
  const get = (token) => pub.get('/quality/sheets/by-qr/', {
    params: { qr },
    headers: {
      ...(key ? { 'X-Passport-Key': key } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  for (const src of PASSPORT_SOURCES) {
    if (!src.get(src.a) && !src.get(src.r)) continue;
    try {
      let r;
      try { r = await get(src.get(src.a)); }
      catch (e) {
        if (e.response?.status !== 401) throw e;
        if (!(await refreshSource(src))) throw e;
        r = await get(src.get(src.a));
      }
      r.data = { ...r.data, auth_source: src.name };
      return r;
    } catch (e) {
      if (e.response?.status !== 401) throw e;
      // Вход негоден. Свой, паспортный, стираем; чужие (MES, /ai) не трогаем.
      if (src.own) { ls.del(PA); ls.del(PR); ls.del(PU); }
    }
  }
  const r = await get('');
  r.data = { ...r.data, auth_source: '' };
  return r;
}

export const passportAuth = {
  // Кто вошёл на этом телефоне (имя для шапки) или ''.
  user: () => (ls.get(PR) ? (ls.get(PU) || 'вход выполнен') : ''),
  // Бросает ошибку с .response.status; пароль не пишем ни в адрес, ни в лог.
  login: async (username, password) => {
    const { data } = await pub.post('/auth/login/', { username, password });
    // Служебная учётка обмена 1С — не человек: её пароль виден в конфигураторе
    // 1С, и полный токен с её правами на запись не должен неделю лежать в
    // телефоне. Отказываем, как и Login.js; ничего не сохраняем.
    const { data: me } = await pub.get('/production/me/', {
      headers: { Authorization: `Bearer ${data.access}` } });
    if (me?.role === 'service') {
      const err = new Error('service');
      err.code = 'service';
      throw err;
    }
    ls.set(PA, data.access);
    ls.set(PR, data.refresh);
    ls.set(PU, username);
  },
  logout: () => { ls.del(PA); ls.del(PR); ls.del(PU); },
};

// --- Auth ---
export const authAPI = {
  login: (username, password) => api.post('/auth/login/', { username, password }),
};

// --- Служебное: пометка на удаление (как в 1С) ---
// Удаление в два шага: пометить → администратор удаляет помеченное.
export const adminToolsAPI = {
  getEntities:  ()       => api.get('/admintools/entities/'),
  getDocuments: (params) => api.get('/admintools/documents/', { params }),
  mark:         (entity, ids, reason) => api.post('/admintools/mark/',   { entity, ids, reason }),
  unmark:       (entity, ids)         => api.post('/admintools/unmark/', { entity, ids }),
  deleteMarked: (entity, ids)         => api.post('/admintools/delete/', { entity, ids }),

  // Остаток заново — админ завода: работа не трогается, меняется только число
  stockResetInfo: ()      => api.get('/admintools/stock-reset/'),
  stockReset:     (data)  => api.post('/admintools/stock-reset/', data || {}),

  // Ниже — только владелец платформы. Без права адреса отвечают «не найдено».
  backups:      ()        => api.get('/admintools/backup/'),
  makeBackup:   (reason)  => api.post('/admintools/backup/', { reason }),
  restore:      (name)    => api.post('/admintools/restore/', { name }),
  systemInfo:   ()        => api.get('/admintools/system-reset/'),
  systemReset:  ()        => api.post('/admintools/system-reset/'),
};

export const integrationAPI = {
  getDashboard: () => api.get('/integration/dashboard/'),
  // Остаток живёт от обмена до обмена — надо видеть, когда он встал
  stockFreshness: () => api.get('/integration/stock-freshness/'),
  stockRefresh:   () => api.post('/integration/stock-refresh/'),
  getStockMovement: (params) => api.get('/integration/stock-movement/', { params }),
  getConfig:    () => api.get('/integration/config/'),
  saveConfig:   (data) => api.put('/integration/config/', data),
  // Состояние обмена списком дел: что сломалось, из-за чего и что сделать
  getHealth:    () => api.get('/integration/health/'),
  // Лента обмена словами: что ушло, что пришло, чем кончилось
  getJournal:   (limit) => api.get('/integration/journal/', { params: { limit } }),
  // Убрать из очереди смены, за которыми не осталось ни одной строки
  dropEmptyShifts: () => api.post('/integration/outbox/drop-empty/'),
  // Вернуть в очередь работу, которой в 1С не было: очередь — не архив
  requeueMissing: () => api.post('/integration/outbox/requeue-missing/'),
  // Отменить лишнее в очереди: по одному, по виду документа или всё до даты.
  // {ids:[...]} | {entity:'nomenclature'} | {before:'ISO'} | {all:true}
  // {restore:true, ...} — вернуть отменённое обратно в очередь
  skipOutbox: (body) => api.post('/integration/outbox/skip/', body),
};

// --- Warehouse ---
export const warehouseAPI = {
  getNomenclatures:  (params) => api.get('/warehouse/nomenclatures/', { params }),
  // Папки справочника — те же, что у бухгалтера в 1С
  getFolders:        (params) => api.get('/warehouse/folders/', { params }),
  getRawMaterials:   ()       => api.get('/warehouse/nomenclatures/raw-materials/'),
  getFinishedProducts:()      => api.get('/warehouse/nomenclatures/finished-products/'),
  createNomenclature:(data)   => api.post('/warehouse/nomenclatures/', data),
  updateNomenclature:(id, data) => api.patch(`/warehouse/nomenclatures/${id}/`, data),
  setMinStock:       (id, val) => api.patch(`/warehouse/nomenclatures/${id}/set-min-stock/`, { min_stock: val }),
  createStockEntry:  (data)    => api.post('/warehouse/stock-entries/create/', data),
  getReceipts:       ()        => api.get('/warehouse/receipts/'),
  createReceipt:     (data)    => api.post('/warehouse/receipts/', data),
  cancelReceipt:     (id)      => api.patch(`/warehouse/receipts/${id}/cancel/`),
  getTextures:        (params) => api.get('/warehouse/textures/', { params }),
  // Заводы-изготовители плиты и плёнки: ?kind=dsp|film — что нужно прессу
  getMaterialMakers:  (params) => api.get('/warehouse/material-makers/', { params }),
  createMaterialMaker: (data) => api.post('/warehouse/material-makers/', data),
  updateMaterialMaker: (id, data) => api.patch(`/warehouse/material-makers/${id}/`, data),
  // Заводы, уже названные в справочнике 1С (в артикуле или в «Производителе»)
  makerCandidates:    () => api.get('/warehouse/material-makers/candidates/'),
  collectMakers:      (items) => api.post('/warehouse/material-makers/collect/', { items }),
  createTexture:      (data) => api.post('/warehouse/textures/', data),
  updateTexture:      (id, data) => api.patch(`/warehouse/textures/${id}/`, data),
  getStockSummary:    (params) => api.get('/warehouse/stock-summary/', { params }),
  // Остатки под сверку с 1С: с кодами и разрезом по складам
  getBalances:        (params) => api.get('/warehouse/balances/', { params }),
  // Взаимозаменяемое сырьё: один лист от разных поставщиков — разные позиции
  getGroups:      ()            => api.get('/warehouse/substitute-groups/'),
  createGroup:    (data)        => api.post('/warehouse/substitute-groups/', data),
  updateGroup:    (id, data)    => api.patch(`/warehouse/substitute-groups/${id}/`, data),
  deleteGroup:    (id)          => api.delete(`/warehouse/substitute-groups/${id}/`),
  addToGroup:     (id, data)    => api.post(`/warehouse/substitute-groups/${id}/add/`, data),
  updateGroupItem:(id, itemId, data) =>
    api.patch(`/warehouse/substitute-groups/${id}/item/${itemId}/`, data),
  removeGroupItem:(id, itemId)  =>
    api.delete(`/warehouse/substitute-groups/${id}/item/${itemId}/`),
  groupProblems:  ()            => api.get('/warehouse/substitute-groups/problems/'),
  previewAutobuild:(params)     => api.get('/warehouse/substitute-groups/autobuild/', { params }),
  runAutobuild:   (data)        => api.post('/warehouse/substitute-groups/autobuild/', data),
  // Обратный разрез: от склада — что на нём лежит
  getStockByWarehouse: () => api.get('/warehouse/by-warehouse/'),
  // Короткие имена для экранов цеха: полное из 1С там не читается
  getShortNames:      (params) => api.get('/warehouse/short-names/', { params }),
  saveShortName:      (data)   => api.post('/warehouse/short-names/', data),
  getStockEntries:    (params) => api.get('/warehouse/stock-entries/', { params }),
  getWarehouses:      () => api.get('/warehouse/warehouses/'),
  // Роль склада в производстве: куда приходуется выпуск, откуда списывается сырьё
  updateWarehouse:    (id, data) => api.patch(`/warehouse/warehouses/${id}/`, data),
  createWarehouse:    (data)     => api.post('/warehouse/warehouses/', data),
  getImportTemplates: () => api.get('/warehouse/import-templates/'),
  importStep1: (formData) =>
    api.post('/warehouse/import/step1/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  importStep2: (data) => api.post('/warehouse/import/step2/', data),
  importStep3: (data) => api.post('/warehouse/import/step3/', data),
};

// --- Sales ---
export const salesAPI = {
  getRecipes:        ()           => api.get('/sales/recipes/'),
  // Продукция без рецепта, которую уже делают или начнут вот-вот
  getMissingRecipes: (params)     => api.get('/sales/recipes/missing/', { params }),
  // Рецепты пачкой: плита зависит от толщины, плёнка — от декора,
  // поэтому выбирают раз на толщину и раз на декор, а не на каждую позицию
  suggestRecipes:    (params)     => api.get('/sales/recipes/suggest/', { params }),
  applyRecipes:      (data)       => api.post('/sales/recipes/apply/', data),
  // Смены, закрытые без рецепта, ничего не списали — досчитываем задним числом
  previewBackfill:   ()           => api.get('/sales/recipes/backfill/'),
  runBackfill:       ()           => api.post('/sales/recipes/backfill/'),
  createRecipe:      (data)       => api.post('/sales/recipes/', data),
  updateRecipe:      (id, data)   => api.put(`/sales/recipes/${id}/`, data),
  deleteRecipe:      (id)         => api.delete(`/sales/recipes/${id}/`),
  checkAvailability: (productId, quantity) =>
    api.get('/sales/recipes/check-availability/', { params: { product_id: productId, quantity } }),
  // search — поиск НА СЕРВЕРЕ: без него список приходит по своему цеху,
  // и заказчик, работавший только с соседним, в нём не находится.
  getClients:        (type, search, all) => api.get('/sales/clients/', {
    params: { ...(type ? { type } : {}), ...(search ? { search } : {}),
              ...(all ? { all: 1 } : {}) } }),
  createClient:      (data)       => api.post('/sales/clients/', data),
  updateClient:      (id, data)   => api.patch(`/sales/clients/${id}/`, data),
  getOrders:         (params)     => api.get('/sales/orders/', { params }),
  getOrder:          (id)         => api.get(`/sales/orders/${id}/`),
  // Состав заказа с ответом на единственный вопрос технолога: эту строку
  // закрывает склад или её надо прессовать. Отдельным запросом, а не полем
  // списка: раскладку смотрят по одному заказу, а список бывает в сотни строк.
  orderBreakdown:    (id)         => api.get(`/sales/orders/${id}/breakdown/`),
  createOrder:       (data)       => api.post('/sales/orders/', data),
  changeOrderStatus: (id, status) => api.patch(`/sales/orders/${id}/change-status/`, { status }),
  // Менеджер разрешает/отзывает отгрузку — до этого кладовщик заказ не берёт
  allowShipping:     (id, allowed) => api.patch(`/sales/orders/${id}/allow-shipping/`, { allowed }),
  // Закрыть заказ, не отгружая: товар увезли отдельной отгрузкой, а заказ
  // держит резерв сырья и мешает брать новые
  closeOrder:        (id, reason)  => api.patch(`/sales/orders/${id}/close/`, { reason }),
  editOrder:         (id, data)   => api.patch(`/sales/orders/${id}/edit/`, data),
  getProductsCatalog:()          => api.get('/sales/orders/products-catalog/'),
  approveOrder:      (id, data)   => api.patch(`/sales/orders/${id}/approve/`, data || {}),
  rejectOrder:       (id)         => api.patch(`/sales/orders/${id}/reject/`),
  // Один адрес на оба кода: длинный из QR и короткий, набранный руками
  trackOrder:        (code)         => api.get(`/sales/track/${code}/`),
  // Ссылка и QR для клиента. base — адрес ЭТОГО окна: сервер за nginx видит
  // свой внутренний хост и собрал бы ссылку, которая никуда не ведёт.
  getTrackingLink:   (id)           => api.get(`/sales/orders/${id}/tracking-link/`,
                                        { params: { base: window.location.origin } }),
  // Табло в офисе — открывается по ключу, без логина
  getTablo:          (token)        => api.get(`/sales/tablo/${token}/`,
                                        { params: { base: window.location.origin } }),
  getTabloKeys:      ()             => api.get('/sales/tablo-keys/'),
  createTabloKey:    (data)         => api.post('/sales/tablo-keys/', data),
  deleteTabloKey:    (id)           => api.delete(`/sales/tablo-keys/${id}/`),
  // Бот уведомлений: токен от @BotFather. base — адрес этого окна, из него
  // собирается webhook, а сервер за nginx знает только внутренний хост.
  getTelegram:       ()             => api.get('/sales/telegram-setup/'),
  saveTelegram:      (token)        => api.post('/sales/telegram-setup/',
                                        { token, base: window.location.origin }),
  disableTelegram:   ()             => api.post('/sales/telegram-setup/', { disable: true }),
  getFeasibilityReport: (params)  => api.get('/sales/orders/feasibility_report/', { params }),
  // Бланк менеджера: декоры строками, матрицы столбцами. Число в клетке —
  // сколько выпущено; пока не выпущено — план, и на бумаге он другой.
  getOrderSheet:     (params)     => api.get('/sales/orders/print-sheet/', { params }),
  // Заказ закрывается складом: прессовать нечего, но выпустить его со двора
  // должен тот, кто видит оплату и обещания, — не тот, кто завёл заказ.
  allowShipment:     (id) => api.post(`/sales/orders/${id}/allow-shipment/`),
  orderToProduction: (id) => api.post(`/sales/orders/${id}/to-production/`),
};

// --- Production ---
// Уборка тестовых данных. Сначала показать, что исчезнет и что вернётся на
// склад, и только отдельным подтверждением — удалить.
export const testDataAPI = {
  list:    ()      => api.get('/admintools/test-data/'),
  preview: (body)  => api.post('/admintools/test-data/', { ...body, confirm: false }),
  wipe:    (body)  => api.post('/admintools/test-data/', { ...body, confirm: true }),
};

// Юк хати — распоряжение на отгрузку. Один документ между офисом и складом
// вместо накладной, написанной от руки и переданной из рук в руки.
export const shipNoteAPI = {
  list:    (params)   => api.get('/sales/shipment-notes/', { params }),
  one:     (id)       => api.get(`/sales/shipment-notes/${id}/`),
  // Что можно везти этому клиенту: незакрытые заказы и остаток по каждой позиции
  sources: (clientId) => api.get('/sales/shipment-notes/',
                                 { params: { mode: 'orders', client: clientId } }),
  issue:   (data)     => api.post('/sales/shipment-notes/', data),
  cancel:  (id)       => api.post(`/sales/shipment-notes/${id}/`, {}),
  // Бланк открывается ссылкой без токена — теперь он закрыт входом, поэтому
  // тянем его запросом с токеном и показываем из blob. Окно открываем сразу,
  // иначе браузер заблокирует его как всплывающее после ожидания ответа.
  print: async (id) => {
    const w = window.open('', '_blank');
    try {
      const resp = await api.get(`/sales/shipment-notes/${id}/print/`, { responseType: 'blob' });
      const url  = URL.createObjectURL(new Blob([resp.data], { type: 'text/html;charset=utf-8' }));
      if (w) w.location.href = url; else window.open(url, '_blank');
    } catch (e) {
      if (w) w.close();
      throw e;
    }
  },
};

// Брак ДСП. Склад считает его не по сменам, а по дню: битые плиты
// складывают в один угол и разбирают вечером.
export const dspDefectAPI = {
  list:   (date)  => api.get('/warehouse/dsp-defect/', { params: { date } }),
  add:    (data)  => api.post('/warehouse/dsp-defect/', data),
  undo:   (id)    => api.post('/warehouse/dsp-defect/', { undo_id: id }),
};

export const productionAPI = {
  getMe:            ()           => api.get('/production/me/'),
  // Журнал действий: кто, когда и что сделал
  getAudit:         (params)     => api.get('/production/audit/', { params }),
  changePassword:   (data)       => api.patch('/production/me/', data),
  updateProfile:    (data)       => api.patch('/production/me/', data),
  getPresses:     () => api.get('/production/presses/'),
  getPress:       (id) => api.get(`/production/presses/${id}/`),
  setPresStatus:  (id, status) => api.patch(`/production/presses/${id}/set-status/`, { status }),
  setPressTexture:(id, textureId) => api.patch(`/production/presses/${id}/set-texture/`, { texture_id: textureId }),
  setPressNorm:   (id, norm) => api.patch(`/production/presses/${id}/set-norm/`, { norm_per_hour: norm }),
  updatePress:    (id, data) => api.patch(`/production/presses/${id}/`, data),
  createPress:    (data) => api.post('/production/presses/', data),
  // Пресс встал: замена матрицы, ремонт, обед. Работу считали по нажатиям
  // «+1», и сорок минут тишины ничем не отличались от сорока минут поломки.
  stopPress:      (id, data) => api.post(`/production/presses/${id}/stop/`, data),
  resumePress:    (id) => api.patch(`/production/presses/${id}/resume/`),
  // Справочник причин: цех у каждого свой, список правит администратор
  getStopReasons:    (params) => api.get('/production/stop-reasons/', { params }),
  createStopReason:  (data) => api.post('/production/stop-reasons/', data),
  updateStopReason:  (id, data) => api.patch(`/production/stop-reasons/${id}/`, data),
  deleteStopReason:  (id) => api.delete(`/production/stop-reasons/${id}/`),
  getQueue:       (params) => api.get('/production/queue/', { params }),
  patchQueueItem: (id, data) => api.patch(`/production/queue/${id}/`, data),
  // Убрать ошибочную позицию из очереди — вместе со строкой заказа:
  // одну очередь удалить мало, позиция вернётся при согласовании
  removeQueueItem: (id, reason) =>
    api.post(`/production/queue/${id}/remove/`, { reason }),
  // То же самое до очереди — ошибку видно уже в таблице согласования
  removeOrderItem: (id, reason) =>
    api.post(`/production/order-items/${id}/remove/`, { reason }),
  // Закрыть заказы отмеченных строк «Что запустить» (только админ):
  // ответ { closed: [номер], errors: [{order, error}] }
  closeSelectedOrders: (itemIds) =>
    api.post('/production/orders/close-selected/', { item_ids: itemIds }),
  startJob:       (queueItemId, data = {}) => api.patch(`/production/queue/${queueItemId}/start/`, data),
  // Позиции, сделанные не в свою смену: технолог переносит или оставляет
  shiftMismatch:  ()      => api.get('/production/shift-mismatch/'),
  resolveShift:   (id, action) =>
    api.post('/production/shift-mismatch/', { queue_item_id: id, action }),
  completeJob:    (queueItemId, data) => api.patch(`/production/queue/${queueItemId}/complete/`, data),
  // Замена плиты/плёнки посреди работы: action = open | save | cancel
  material:       (queueItemId, data) => api.post(`/production/queue/${queueItemId}/material/`, data),
  incrementJob:   (queueItemId) => api.patch(`/production/queue/${queueItemId}/increment/`),
  // Лишнее нажатие «+1» снимают там же, у пресса
  decrementJob:   (queueItemId) => api.patch(`/production/queue/${queueItemId}/decrement/`),
  getPressJobs:   (params) => api.get('/production/press-jobs/', { params }),
  // Смены этого пресса, которые склад ещё не забрал — их цифру можно поправить
  awaitingJobs:   (pressId) => api.get('/production/press-jobs/awaiting/', { params: { press_id: pressId } }),
  // Расход ДСП и плёнки считается по выпуску, поэтому цифру чинит пресс,
  // а не склад: поправишь только приёмку — сырьё останется списанным зря
  correctQuantity: (jobId, data) =>
    api.patch(`/production/press-jobs/${jobId}/correct-quantity/`, data),
  getPressJob:    (id)     => api.get(`/production/press-jobs/${id}/`),
  setJobParams: (id, data) => api.patch(`/production/queue/${id}/set-params/`, data),
  // Опознать человека у пресса по ПИНу
  pressPin:     (pin) => api.post('/production/press-pin/', { pin }),
  markGrade:   (jobId, gradeTypeId) =>
    api.patch(`/production/press-jobs/${jobId}/mark-grade/`, { grade_type_id: gradeTypeId }),
  unmarkGrade: (jobId, gradeTypeId) =>
    api.patch(`/production/press-jobs/${jobId}/unmark-grade/`, { grade_type_id: gradeTypeId }),
  // Плёнку считают, а не отмечают: рулон рвётся сразу пачкой
  setGrade: (jobId, gradeTypeId, quantity) =>
    api.patch(`/production/press-jobs/${jobId}/set-grade/`,
              { grade_type_id: gradeTypeId, quantity }),
  sendToWarehouse: (jobId) =>
    api.post(`/production/press-jobs/${jobId}/send-to-warehouse/`),

  // ── Заявка на производство ───────────────────────────────────────────
  // Технолог согласовывает не по одному заказу, а таблицей: порядок строк
  // в таблице становится порядком очереди у оператора.
  // Эталонные режимы: один список вместо двух разделов справочника
  getEtalon:        ()      => api.get('/production/etalon/'),
  saveEtalon:       (data)  => api.post('/production/etalon/', data),
  // Только там, где эталона нет. Переписать отпечатанное нельзя ничем.
  stampMissingEtalon: (data) => api.post('/production/etalon/stamp-missing/', data),
  getApprovalTable: ()      => api.get('/production/approval-table/'),
  approveBatch:     (data)  => api.post('/production/approve-batch/', data),
  // Автопланировщик: настройки цеха, черновик плана, применить/отклонить
  getPlannerConfig:   ()      => api.get('/production/planner/config/'),
  savePlannerConfig:  (data)  => api.put('/production/planner/config/', data),
  plannerPropose:     (data)  => api.post('/production/planner/propose/', data || {}),
  plannerApply:       (id, data) => api.post(`/production/planner/proposals/${id}/apply/`, data),
  plannerDiscard:     (id)    => api.post(`/production/planner/proposals/${id}/discard/`),
  plannerCompare:     (a, b)  => api.get('/production/planner/proposals/compare/', { params: { a, b } }),
  getRequests:      (params) => api.get('/production/requests/', { params }),
  // Отчёт за день: все заявки одним листом, по сменам и прессам
  getDailyReport:   (params) => api.get('/production/requests/daily/', { params }),
  getShiftBlank:    (params) => api.get('/production/press-jobs/shift-blank/', { params }),
  // Куда у пресса уходит время: работа, замена матрицы, ремонт, обед
  getPressTime:     (params) => api.get('/production/press-time/', { params }),
  // Реестр выпуска: продукт → смены → режим и листы
  getRegistry:      (params) => api.get('/production/registry/', { params }),
  getTelemetry:     (pressId) => api.get('/production/telemetry/latest/', { params: { press_id: pressId } }),
  getPressDevices:  () => api.get('/production/telemetry/devices/'),
  savePressDevice:  (data) => api.post('/production/telemetry/devices/', data),
  // Сообщения с пресса, которые MES принять не смог: листы отпрессованы, а
  // причина отказа не в самом сообщении — их разбирает администратор
  getDeviceRejects: (status) =>
    api.get('/production/device-rejects/', { params: { status } }),
  // job — смену называет человек: бывает, что найти её нечем (start не
  // приняли вовсе), а лист отпрессован и лежит на складе с биркой
  resolveDeviceReject: (id, action, note, job) =>
    api.post('/production/device-rejects/', { id, action, note, job }),
  // Один пресс — один моноблок: терминал занимает пресс и подтверждает, что жив
  claimPress:       (id, clientId) => api.post(`/production/presses/${id}/claim/`, { client_id: clientId }),
  releasePress:     (id, clientId) => api.post(`/production/presses/${id}/release/`, { client_id: clientId }),
  // Отпустить пресс при ЗАКРЫТИИ вкладки. Обычный axios на выгрузке страницы
  // браузер отменяет — запрос не успевает уйти; keepalive держит его живым.
  releasePressBeacon: (id, clientId) => {
    try {
      return fetch(`${API_BASE}/production/presses/${id}/release/`, {
        method: 'POST', keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('access_token') || ''}`,
        },
        body: JSON.stringify({ client_id: clientId }),
      });
    } catch (e) { return null; }
  },
  getPressSessions: (clientId) => api.get('/production/presses/sessions/', { params: { client_id: clientId } }),
  getRequest:       (id)     => api.get(`/production/requests/${id}/`),
  updateRequest:    (id, data) => api.patch(`/production/requests/${id}/`, data),
  // Правка выданной заявки: request → (оператор) stop → save → finish
  editRequest:      (id, data) => api.post(`/production/requests/${id}/edit/`, data),
  getSuggestions:   (kind)  => api.get('/production/suggestions/', { params: { kind } }),
  addSuggestion:    (kind, value) => api.post('/production/suggestions/', { kind, value }),
  deleteSuggestion: (id)    => api.delete('/production/suggestions/', { params: { id } }),
};

// --- Quality ---
export const qualityAPI = {
  getPallets:    (params) => api.get('/quality/pallets/', { params }),
  createPallet:  (data)   => api.post('/quality/pallets/', data),
  getPalletByQR: (qrCode) => api.get('/quality/pallets/by-qr/', { params: { qr: qrCode } }),
  shipPallet:    (id, note) => api.patch(`/quality/pallets/${id}/ship/`, { note }),
};

// --- Sheet (alohida list) ---
export const sheetAPI = {
  create:     (data)    => api.post('/quality/sheets/', data),
  // key — пароль к закрытой части паспорта; заголовком, не в адресе.
  // Вход сотрудника (MES / паспорта / /ai) подставляется отсюда же — см. passportAuth.
  getByQr:    (qr, key) => passportFetch(qr, key),
  lookup:     (qr)      => api.get('/quality/sheets/lookup/', { params: { qr } }),
  regrade:    (data)    => api.post('/quality/sheets/regrade/', data),
  // Ошибочную пересортицу не стираем — пишем обратную запись
  revertGrade: (changeId) => api.post('/quality/sheets/regrade/revert/', { change_id: changeId }),
  // Общий журнал пересортицы: листы перебивают с нескольких планшетов сразу
  gradeLog:   (limit)   => api.get('/quality/sheets/grade-log/', { params: { limit: limit || 20 } }),
  // Пересортица через утверждение: кладовщик подаёт пакет, директор решает.
  // Сорт меняется только после «да» — до этого лист остаётся тем, чем был.
  gradeRequests:  (params) => api.get('/quality/grade-requests/', { params }),
  gradeRequest:   (data)   => api.post('/quality/grade-requests/', data),
  gradeRequestPhoto: (id, file) => {
    const fd = new FormData();
    fd.append('image', file);
    return api.post(`/quality/grade-requests/${id}/photo/`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  gradeRequestDecide: (id, approve, note) =>
    api.post(`/quality/grade-requests/${id}/decide/`, { approve, note }),
  // Приёмка выпуска из цеха: что допрессовали и ждёт склад
  acceptance:  ()        => api.get('/quality/acceptance/'),
  // Что уже приняли: очередь показывает только непринятое, а спрашивают
  // ровно про принятое — «эту партию вчера завели?»
  acceptanceDone: (params) => api.get('/quality/acceptance/done/', { params }),
  // Из-за чего лист не высшего сорта — справочник правит администратор
  defectReasons: (all) => api.get('/quality/defect-reasons/',
    { params: all ? { all: 1 } : {} }),
  createDefectReason: (data)     => api.post('/quality/defect-reasons/', data),
  updateDefectReason: (id, data) => api.patch(`/quality/defect-reasons/${id}/`, data),
  // Бирки на всю стопку одним документом — по странице на лист
  acceptanceLabelsUrl: (jobId) =>
    `${API_BASE}/quality/acceptance/${jobId}/labels/?auto=1`,
  // Одна бирка по коду — когда QR затёрся, а цифры под ним ещё читаются
  labelByCodeUrl: (code) =>
    `${API_BASE}/quality/labels/${encodeURIComponent(code)}/?auto=1`,
  // Склад правит выпуск: ночного оператора утром уже не спросишь
  fixAcceptanceQty: (jobId, quantity, reason) =>
    api.post(`/quality/acceptance/${jobId}/fix-quantity/`, { quantity, reason }),
  // Смена целиком: листы с кодами, отметки оператора, брак материала, история
  acceptanceJob: (jobId) => api.get(`/quality/acceptance/${jobId}/`),
  // Чей это лист: рядом лежат стопки других смен, чужая бирка попадает
  // под сканер сама собой — и брак записался бы на чужую работу
  acceptanceScan: (jobId, code) =>
    api.get(`/quality/acceptance/${jobId}/scan/`, { params: { code } }),
  accept:      (data)    => api.post('/quality/acceptance/', data),
  free:       (params)  => api.get('/quality/sheets/free/', { params }),
};

// --- История смен: цепочка «пресс → склад» ---
export const sessionAPI = {
  list:   (params) => api.get('/quality/sessions/', { params }),
  detail: (id)     => api.get(`/quality/sessions/${id}/`),
};

// --- Pallet yig'ish / ochish ---
export const palletAPI = {
  build:        (data)     => api.post('/quality/pallets/build/', data),
  buildFromJob: (data)     => api.post('/quality/pallets/build-from-job/', data),
  sheets:       (id)       => api.get(`/quality/pallets/${id}/sheets/`),
  removeSheets: (id, data) => api.post(`/quality/pallets/${id}/remove-sheets/`, data),
  addSheets:    (id, data) => api.post(`/quality/pallets/${id}/add-sheets/`, data),
  // Отгрузка по бирке ЛИСТА + количество: списывает с паллет того же
  // продукта и сорта — сначала с паллеты листа, затем с самых старых.
  shipBySheet:   (data)    => api.post('/quality/ship-by-sheet/', data),
};

// ── Отгрузка со склада одним документом ──────────────────────────────────────
// Контрагент обязателен, заказ — нет: склад грузит и то, чего никто не
// заказывал, и сверх заказа.
export const shipmentAPI = {
  create: (data) => api.post('/quality/shipments/', data),
  // Что лежит на складе и это можно отгрузить — когда бирки под рукой нет
  available: (params) => api.get('/quality/shipments/available/', { params }),
  // Отгрузка по распоряжению офиса: товар назван, сканировать нечего
  byNote: (noteId) => api.post('/quality/shipments/by-note/', { note: noteId }),
};
// ── Birka chop etish ──────────────────────────────────────────────────────────
// Барча ярлыки печатаются через СКРЫТЫЙ iframe — новое окно НЕ открывается.
// Задания ставятся в очередь: оператор может жать «+1» подряд, ярлыки уйдут
// на принтер по одному, не перебивая друг друга.
let printQueue = Promise.resolve();
const enqueuePrint = (task) => {
  printQueue = printQueue.then(task, task);
  return printQueue;
};

export const printViaIframe = (url) => new Promise((resolve) => {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
  let finished = false;
  const cleanup = () => {
    if (finished) return;
    finished = true;
    setTimeout(() => { if (document.body.contains(iframe)) document.body.removeChild(iframe); }, 2000);
    resolve();
  };
  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      // iframe'дан чоп этиб бўлмаса — эски усул: янги ойна (у ўзи чоп этади)
      const sep = url.includes('?') ? '&' : '?';
      window.open(`${url}${sep}auto=1`, '_blank', 'width=460,height=340');
    }
    setTimeout(cleanup, 700);
  };
  iframe.onerror = cleanup;
  iframe.src = url;
  document.body.appendChild(iframe);
  setTimeout(cleanup, 10000);   // хеч нарса бўлмаса — навбат тиқилиб қолмасин
});

// opts: { copies = 1, newWindow = false }
const printSheetLabelUrl = (url, opts = {}) => {
  const { copies = 1, newWindow = false } = opts;
  if (newWindow) {
    const sep = url.includes('?') ? '&' : '?';
    window.open(`${url}${sep}auto=1`, '_blank', 'width=460,height=340');
    return Promise.resolve();
  }
  let last = Promise.resolve();
  for (let i = 0; i < Math.max(1, Number(copies) || 1); i++) {
    last = enqueuePrint(() => printViaIframe(url));
  }
  return last;
};
export const printSheetLabelBig = (id, opts) => printSheetLabelUrl(`${API_BASE}/quality/sheets/${id}/label/?size=big`, opts);
export const printSheetLabel    = (id, opts) => printSheetLabelUrl(`${API_BASE}/quality/sheets/${id}/label/`, opts);
// Мини бирка — 30×20 мм (эни 3 см, бўйи 2 см)
export const printSheetLabelMini = (id, opts) => printSheetLabelUrl(`${API_BASE}/quality/sheets/${id}/label/?size=mini`, opts);
// Ҳар уч ўлчов учун ягона кириш нуқтаси: size = 'mini' | 'small' | 'big'
export const printSheetLabelBySize = (id, size, opts) => {
  if (size === 'big')  return printSheetLabelBig(id, opts);
  if (size === 'mini') return printSheetLabelMini(id, opts);
  return printSheetLabel(id, opts);
};

// --- Pallet label print ---
export const printLabel = async (id) => {
  const resp = await api.get(`/quality/pallets/${id}/label/`, { responseType: 'blob' });
  const url  = URL.createObjectURL(resp.data);
  window.open(url, '_blank');
};
// Автопечать этикетки паллеты — без нового окна, сразу на принтер (скрытый iframe).
// Ярлык требует авторизации, поэтому тянем blob через axios, затем печатаем.
export const printLabelAuto = async (id) => {
  const resp = await api.get(`/quality/pallets/${id}/label/`, { responseType: 'blob' });
  const url  = URL.createObjectURL(resp.data);
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
  iframe.src = url;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    try { iframe.contentWindow.print(); } catch (e) { window.open(url, '_blank'); }
    setTimeout(() => {
      if (document.body.contains(iframe)) document.body.removeChild(iframe);
      URL.revokeObjectURL(url);
    }, 3000);
  };
};

// --- Grade Types ---
export const gradeAPI = {
  getGradeTypes:   (params)    => api.get('/quality/grade-types/', { params }),
  createGradeType: (data)      => api.post('/quality/grade-types/', data),
  updateGradeType: (id, data)  => api.patch(`/quality/grade-types/${id}/`, data),
};

// ТЗ §4: Excel-выгрузка отчёта «Движение ТМЗ»
export const downloadStockMovementXlsx = async (params = {}) => {
  const token = sessionStorage.getItem('access_token');
  // Пустое выбрасываем: URLSearchParams превращает undefined в строку
  // «undefined», и на сервер уходило ?warehouse=undefined — фильтр по складу
  // с несуществующим номером вместо «все склады».
  const clean = Object.fromEntries(
    Object.entries({ ...params, format: 'xlsx' })
      .filter(function (kv) { return kv[1] !== undefined && kv[1] !== null && kv[1] !== ''; }));
  const query = new URLSearchParams(clean).toString();
  const resp = await fetch(`${API_BASE}/integration/stock-movement/?${query}`,
    { headers: { Authorization: `Bearer ${token}` } });
  if (!resp.ok) throw new Error('Export failed');
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tmz_movement_${params.date_from || ''}_${params.date_to || ''}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};

// --- Branches ---
// Панель поставщика программы. Клиент этих адресов не видит: без права они
// отвечают «не найдено», а не «нет прав».
// Настройки ИИ: только владелец платформы (is_superuser). Ключ наружу не отдаётся.
export const aiAdminAPI = {
  list:     ()           => api.get('/ai/admin/settings/'),
  get:      (target)     => api.get(`/ai/admin/settings/${target}/`),
  save:     (target, d)  => api.put(`/ai/admin/settings/${target}/`, d),
  test:     (target, d)  => api.post(`/ai/admin/settings/${target}/test/`, d || {}),
  resetKey: (target)     => api.post(`/ai/admin/settings/${target}/reset-key/`),
};

// Пороги светофора ИИ: админ клиента и владелец платформы.
export const aiThresholdsAPI = {
  get:  ()  => api.get('/ai/thresholds/'),
  save: (d) => api.put('/ai/thresholds/', d),
};

export const platformAPI = {
  orgs:        ()            => api.get('/production/platform/orgs/'),
  createOrg:   (data)        => api.post('/production/platform/orgs/', data),
  updateOrg:   (id, data)    => api.patch(`/production/platform/orgs/${id}/`, data),
  addBranch:   (id, data)    => api.post(`/production/platform/orgs/${id}/branches/`, data),
  // Переименовать цех. Код цеха при этом не меняется: по нему он сопоставлен
  // с подразделением в 1С.
  saveBranch:  (id, data)    => api.patch(`/production/platform/orgs/${id}/branches/`, data),
  addAdmin:    (id, data)    => api.post(`/production/platform/orgs/${id}/admin/`, data),
  resetPass:   (uid, data)   => api.post(`/production/platform/users/${uid}/password/`, data),
  // Доложить справочник тем, кого завели до того, как он стал класться сам
  seedOrg:     (id)          => api.post(`/production/platform/orgs/${id}/seed/`),
};

// Цеха рядом: вопрос «где хуже» одним взглядом, без переключений и памяти
export const compareAPI = {
  branches: (params) => api.get('/analytics/branches/', { params }),
};

export const branchAPI = {
  getList: () => api.get('/warehouse/branches/'),
  update:  (id, data) => api.patch(`/warehouse/branches/${id}/`, data),
};

// --- In-Transit Stock ---
// --- Transfers ---
export const transferAPI = {
  getList:  (params) => api.get('/warehouse/transfers/', { params }),
  create:   (data)   => api.post('/warehouse/transfers/', data),
  confirm:  (id, data) => api.patch(`/warehouse/transfers/${id}/confirm/`, data),
  cancel:   (id)     => api.patch(`/warehouse/transfers/${id}/cancel/`),
};

// --- Users (admin) ---
export const usersAPI = {
  getList:   ()           => api.get('/production/users/'),
  create:    (data)       => api.post('/production/users/', data),
  update:    (id, data)   => api.patch(`/production/users/${id}/`, data),
  deactivate:(id)         => api.delete(`/production/users/${id}/`),
};

// --- Advanced Analytics ---
export const advancedAPI = {
  get: (period = 30) => api.get('/analytics/advanced/', { params: { period } }),
  // Откуда берётся брак: разрез по партии, плёнке, прессу, режиму
  defects: (days = 90) => api.get('/analytics/defects/', { params: { days } }),
  // Листы, стоящие за цифрой в разборе: какой лист, кто выпустил, когда,
  // на каком прессе, в каком режиме. Отчёт кончался на числе «3».
  defectSheets: (params) => api.get('/analytics/defects/sheets/', { params }),
  // Насколько цех уходит от эталонного режима. Порог — параметром: его
  // меняют прямо на экране, чтобы посмотреть, что даёт каждое значение.
  deviation: (params) => api.get('/analytics/deviation/', { params }),
};

// --- Разбор качества (технолог по качеству) ---
//
// Один и тот же `params` во всех четырёх: что бы человек ни выбрал в фильтре,
// выводы, срез и таблица листов считаются по ОДНОЙ выборке. Собирать условия
// на экране по-разному для разных ручек нельзя — цифры разойдутся, и верить
// перестанут обеим.
export const qualityAnalyticsAPI = {
  sheets:   (params) => api.get('/analytics/sheets/', { params }),
  // Из чего выбирать в фильтре — только то, что реально встретилось за период
  facets:   (params) => api.get('/analytics/sheets/facets/', { params }),
  group:    (params) => api.get('/analytics/sheets/group/', { params }),
  // Куда смотреть: наблюдения, сдвиг режима, коридоры, здоровье данных
  insights: (params) => api.get('/analytics/sheets/insights/', { params }),
  // Выгрузка идёт fetch'ем с токеном, а не ссылкой: ссылку браузер откроет
  // без заголовка Authorization и получит 401 — молча, пустым файлом.
  download: async (params = {}) => {
    const token = sessionStorage.getItem('access_token');
    const q = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === '' || v === null || v === undefined) return;
      (Array.isArray(v) ? v : [v]).forEach(x => q.append(k, x));
    });
    const resp = await fetch(
      `${API_BASE_URL}/analytics/sheets/export/?${q.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) throw new Error('Export failed');
    const blob = await resp.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'reestr-proizvodstva.xlsx';
    link.click();
    URL.revokeObjectURL(link.href);
  },
};

// --- Analytics ---
export const analyticsAPI = {
  getDashboard:      (period = 7) => api.get('/analytics/dashboard/', { params: { period } }),
  getProductionBoard:()           => api.get('/analytics/board/'),
  getEfficiency:     (params)     => api.get('/analytics/efficiency/', { params }),
};

// --- Заказы по дням ---
const obdQuery = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === '' || v === null || v === undefined) return;
    (Array.isArray(v) ? v : [v]).forEach(x => q.append(k, x));
  });
  // fetch идёт мимо перехватчика axios — переключатель цеха кладём сами
  const branch = sessionStorage.getItem('selected_branch');
  if (branch && branch !== 'all') q.set('branch', branch);
  return q.toString();
};

export const ordersByDayAPI = {
  options: () => api.get('/analytics/orders-by-day/options/'),
  report:  (params) => api.get('/analytics/orders-by-day/', {
    params, paramsSerializer: { indexes: null } }),
  // Выгрузка fetch'ем с токеном: ссылка ушла бы без Authorization
  download: async (params = {}) => {
    const token = sessionStorage.getItem('access_token');
    const resp = await fetch(
      `${API_BASE_URL}/analytics/orders-by-day/export/?${obdQuery(params)}`,
      { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) throw new Error('Export failed');
    const blob = await resp.blob();
    const cd = resp.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename="(.+?)"/);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = m ? m[1] : 'orders_by_day.xlsx';
    link.click();
    URL.revokeObjectURL(link.href);
  },
};

// --- Export (xlsx download) ---
// Адрес берём из API_BASE_URL наверху файла: второй такой же const жил здесь
// и повторял ту же строку — расходились бы они молча.
export const exportAPI = {
  download: async (path, params = {}) => {
    const token = sessionStorage.getItem('access_token');
    const query = new URLSearchParams(params).toString();
    const url   = `${API_BASE_URL}/analytics/export/${path}/${query ? '?' + query : ''}`;
    const resp  = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) throw new Error('Export failed');
    const blob  = await resp.blob();
    const cd    = resp.headers.get('Content-Disposition') || '';
    const match = cd.match(/filename="(.+?)"/);
    const fname = match ? match[1] : `${path}_export.xlsx`;
    const link  = document.createElement('a');
    link.href   = URL.createObjectURL(blob);
    link.download = fname;
    link.click();
    URL.revokeObjectURL(link.href);
  },
  orders:     (params) => exportAPI.download('orders',     params),
  pallets:    (params) => exportAPI.download('pallets',    params),
  stock:      ()       => exportAPI.download('stock',      {}),
  production: (params) => exportAPI.download('production', params),
};

export default api;
